import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Box, Menu, MenuItem, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useAuthState } from "react-firebase-hooks/auth";
import SearchIcon from "@mui/icons-material/Search";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { t } from "i18next";
import { auth } from "../firebase";
import useLogout from "../hooks/useLogout";
import useAccountMenu, { renderMenuGroups, withoutPaths } from "../hooks/useAccountMenu";
import { features } from "../utils/features";
import { CustomLogoutIcon } from "./CustomIcons";

export const MOBILE_NAV_HEIGHT = 58;

type Tab = {
  label: string;
  path: string;
  // Where login should bring a guest back to; defaults to the page they are on.
  returnTo?: string;
  icon: React.ReactNode;
  badge?: number;
  match: (pathname: string) => boolean;
};

/**
 * On phones the four things the product is about - searching, matches, conversations
 * and the account - stay one tap away instead of hiding inside the overflow menu.
 */
const MobileBottomNav: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const { groups: accountGroups, unreadCount } = useAccountMenu();
  const [accountAnchorEl, setAccountAnchorEl] = React.useState<null | HTMLElement>(null);

  const logout = useLogout();
  const handleLogout = () => {
    setAccountAnchorEl(null);
    logout();
  };

  if (!isMobile) return null;

  const loginState = { returnTo: location.pathname + location.search + location.hash };

  const tabs: Tab[] = [
    {
      label: t("search", { ns: "words", defaultValue: "Szukaj" }),
      path: "/",
      icon: <SearchIcon sx={{ fontSize: 22 }} />,
      match: (p) => p === "/" || p.startsWith("/searchResult") || p.startsWith("/search-criteria"),
    },
    ...(features.marketplace
      ? [
          {
            label: t("matches", { ns: "words", defaultValue: "Dopasowania" }),
            path: "/sell",
            icon: <HandshakeOutlinedIcon sx={{ fontSize: 22 }} />,
            match: (p: string) => p === "/sell",
          },
          {
            label: "Ogłoszenia",
            path: "/my-listings",
            // PrivateRoute sends a guest through login and back to the listings.
            match: (p: string) => p.startsWith("/my-listings") || p === "/sell/new" || p.startsWith("/listing/"),
            icon: <StorefrontOutlinedIcon sx={{ fontSize: 22 }} />,
          },
        ]
      : [
          // Without selling, the freed slots go to what a buyer comes back for.
          {
            label: t("saved", { ns: "words", defaultValue: "Zapisane" }),
            path: "/subscriptions",
            icon: <BookmarkBorderIcon sx={{ fontSize: 22 }} />,
            match: (p: string) => p === "/subscriptions",
          },
          {
            label: t("favorite", { ns: "words", defaultValue: "Ulubione" }),
            path: "/favorite",
            icon: <FavoriteBorderIcon sx={{ fontSize: 22 }} />,
            match: (p: string) => p === "/favorite",
          },
        ]),
    ...(features.messages
      ? [{
          label: t("messages", { ns: "words", defaultValue: "Wiadomości" }),
          path: user ? "/conversations" : "/login",
          // The guest asked for their inbox, not for the page they happened to be on.
          returnTo: "/conversations",
          icon: <MailOutlineIcon sx={{ fontSize: 22 }} />,
          badge: unreadCount || undefined,
          match: (p: string) => p.startsWith("/conversations"),
        }]
      : []),
  ];
  // The account menu holds whatever has no tab of its own, and the tab lights up on those pages.
  const menuGroups = withoutPaths(accountGroups, tabs.map((tab) => tab.path));
  tabs.push({
    label: t("account", { ns: "words", defaultValue: "Konto" }),
    path: user ? "/settings" : "/login",
    icon: <PersonOutlineIcon sx={{ fontSize: 22 }} />,
    match: (p) => menuGroups.some((group) => group.some((link) => link.path === p)),
  });

  const handleMenuSelect = (path: string) => {
    setAccountAnchorEl(null);
    navigate(path);
  };

  const renderTab = (tab: Tab, index: number) => {
    const active = tab.match(location.pathname);
    const isAccount = index === tabs.length - 1;
    const content = (
      <>
        <Box sx={{ position: "relative", display: "flex" }}>
          {tab.icon}
          {tab.badge ? (
            <Box
              component="span"
              sx={{
                position: "absolute",
                top: -2,
                right: -4,
                minWidth: 8,
                height: 8,
                borderRadius: 999,
                bgcolor: theme.palette.primary.main,
              }}
            />
          ) : null}
        </Box>
        <Box component="span" sx={{ fontSize: 10.5, fontWeight: active ? 700 : 500 }}>
          {tab.label}
        </Box>
      </>
    );

    const sx = {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 0.25,
      py: 0.75,
      border: "none",
      background: "transparent",
      fontFamily: "inherit",
      cursor: "pointer",
      textDecoration: "none",
      color: active ? theme.palette.primary.main : "var(--muted)",
    } as const;

    if (isAccount) {
      return (
        <Box
          key={tab.label}
          component="button"
          type="button"
          aria-label={tab.label}
          aria-haspopup="menu"
          onClick={(e: React.MouseEvent<HTMLElement>) => setAccountAnchorEl(e.currentTarget)}
          sx={sx}
        >
          {content}
        </Box>
      );
    }

    return (
      <Box
        key={tab.label}
        component={Link}
        to={tab.path}
        state={tab.path === "/login" ? (tab.returnTo ? { returnTo: tab.returnTo } : loginState) : undefined}
        aria-current={active ? "page" : undefined}
        sx={sx}
      >
        {content}
      </Box>
    );
  };

  return (
    <>
      <Box
        component="nav"
        aria-label={t("mainNavigation", { ns: "words", defaultValue: "Nawigacja główna" })}
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: (th) => th.zIndex.appBar,
          height: `${MOBILE_NAV_HEIGHT}px`,
          display: "flex",
          alignItems: "stretch",
          bgcolor: "#fff",
          borderTop: "1px solid var(--line)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        {tabs.map(renderTab)}
      </Box>

      <Menu
        anchorEl={accountAnchorEl}
        open={Boolean(accountAnchorEl)}
        onClose={() => setAccountAnchorEl(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {!user && (
          <MenuItem
            onClick={() => {
              setAccountAnchorEl(null);
              navigate("/login", { state: loginState });
            }}
          >
            {t("login", { ns: "action", defaultValue: "Zaloguj się" })}
          </MenuItem>
        )}
        {!user && (
          <MenuItem
            onClick={() => {
              setAccountAnchorEl(null);
              navigate("/register", { state: loginState });
            }}
          >
            {t("register", { ns: "action", defaultValue: "Załóż konto" })}
          </MenuItem>
        )}
        {renderMenuGroups(menuGroups, handleMenuSelect)}
        {user && (
          <MenuItem onClick={handleLogout}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
              <CustomLogoutIcon />
              <Box component="span" sx={{ flex: 1 }}>{t("logOut", { ns: "action", defaultValue: "Wyloguj" })}</Box>
            </Box>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default MobileBottomNav;
